import React from "react";
import { IconButton } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";

export const loadingText = "Loading...";

export function AcreInchPerAcreUnits() {
	return <span>ac-in/ac</span>;
}

export function AcreInchPerYearUnits() {
	return <span>ac-in/yr</span>;
}

export function CO2() {
	return (
		<span>
			CO<sub>2</sub>
		</span>
	);
}

export function N2O() {
	return (
		<span>
			N<sub>2</sub>O
		</span>
	);
}

export function CO2Units() {
	return (
		<span>
			tonnes <CO2 />
			-eq
		</span>
	);
}

export function CO2PerAcreYearUnits() {
	return (
		<span>
			tonnes <CO2 />
			-eq/ac/yr
		</span>
	);
}

export function CO2PerDollarUnits() {
	return (
		<span>
			tonnes <CO2 />
			-eq/$
		</span>
	);
}
export function CO2PerYearUnits() {
	return (
		<span>
			tonnes <CO2 />
			-eq/yr
		</span>
	);
}
export function N2OPerYearUnits() {
	return (
		<span>
			lb <N2O />
			-N/yr
		</span>
	);
}

export function formatResult(value, stripZeroes = false) {
	if (value == null || value === undefined) {
		return loadingText;
	}

	if (typeof value === "number") {
		var precision = 4;

		return stripZeroes ? +value.toFixed(precision) : value.toFixed(precision); //plus sign removes trailing zeroes
	}

	return value;
}

export function formatDollars(value) {
	return (value ?? 0).toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
	});
}

export function getKeyOutputsData(reportData, fundsRequested, handleFundsClick) {
	return [
		{
			label: () => "Pumping GHG Benefits per Growing Season*",
			value: formatResult(reportData.pumpingReport.yearlyGHGBenefits),
			units: <CO2PerYearUnits />,
			colorCoded: true,
		},
		{
			label: () => "N2O GHG Benefits per Growing Season*",
			value: formatResult(reportData.overallReport.convertedN2OBenefits),
			units: <CO2PerYearUnits />,
			colorCoded: true,
		},
		{
			label: () => "Total GHG Benefits per Growing Season*",
			value: formatResult(reportData.overallReport.yearlyGHGBenefits),
			units: <CO2PerYearUnits />,
			colorCoded: true,
		},
		{
			label: () => "GHG Benefits per Acre-Year",
			value: formatResult(reportData.overallReport.ghgBenefitsPerAcreYear),
			units: <CO2PerAcreYearUnits />,
			colorCoded: true,
		},
		{
			label: () => (
				<span style={{ display: "flex", alignItems: "center" }}>
					Total Funds Requested
					<IconButton size="small" onClick={handleFundsClick} style={{ marginLeft: 4 }}>
						<EditIcon fontSize="small" />
					</IconButton>
				</span>
			),
			value: formatDollars(fundsRequested),
		},
		{
			label: () => "Total GHG Emission Reductions per Dollar Requested",
			value: formatResult(reportData.overallReport.emissionReductionsPerFundsRequested),
			units: <CO2PerDollarUnits />,
			colorCoded: true,
		},
		{
			label: () => "Yearly Pre-Project Water Use*",
			value: formatResult(reportData.waterReport.beforeWaterUse / 3),
			units: <AcreInchPerYearUnits />,
		},
		{
			label: () => "Yearly Water Savings*",
			value: formatResult(reportData.waterReport.waterSavings / 3),
			units: <AcreInchPerYearUnits />,
			colorCoded: true,
		},
		{
			label: () => "Water Savings Percentage*",
			value: formatResult(reportData.waterReport.percentSavings) + "%",
			colorCoded: true,
		},
		{
			label: () => "Total Acres Impacted*",
			value: formatResult(reportData.waterReport.totalArea),
			units: "acres",
		},
	];
}

export function getGHGData(reportData, pumpResults) {
	return {
		label: () => "GHG Emissions",
		cardTitle: "GHG Emission Reductions from Pumping System Enhancements in Proposed Project",
		columnHeaders: [
			() => "Pumping System",
			() => (
				<span>
					Pre-Project GHG Emissions (<CO2PerYearUnits />)
				</span>
			),
			() => (
				<span>
					Post-Project GHG Emissions (<CO2PerYearUnits />)
				</span>
			),
			() => (
				<span>
					GHG Emissions Reductions (<CO2PerYearUnits />)
				</span>
			),
			() => (
				<span>
					10-Year Project Benefits (<CO2Units />)
				</span>
			),
		],
		rows:
			pumpResults &&
			pumpResults
				.map((pump) => [
					pump.name,
					formatResult(pump.ghgPre),
					formatResult(pump.ghgPost),
					formatResult(pump.ghgPre - pump.ghgPost),
					formatResult((pump.ghgPre - pump.ghgPost) * 10),
				])
				.concat([
					[
						"Renewable Energy Savings",
						"",
						"",
						reportData.pumpingReport.renewableEnergy,
						reportData.pumpingReport.renewableEnergy * 10,
					],
				]),
		totalsRow: [
			"Total",
			formatResult(reportData.pumpingReport.beforeGHG),
			formatResult(reportData.pumpingReport.afterGHG),
			formatResult(reportData.pumpingReport.yearlyGHGBenefits),
			formatResult(reportData.pumpingReport.tenYearGHGBenefits),
		],
	};
}

export function getN2OData(reportData, fields) {
	return {
		label: () => (
			<span>
				<N2O /> Emissions
			</span>
		),
		cardTitle: "Nitrous Oxide GHG Emission Reductions from Irrigation System Enhancements in Proposed Project",
		columnHeaders: [
			() => "Field",
			() => (
				<span>
					Pre-Project Average <N2O /> Emissions (<CO2Units />)
				</span>
			),
			() => (
				<span>
					Post-Project Average <N2O /> Emissions (<CO2Units />)
				</span>
			),
			() => (
				<span>
					Average Yearly <N2O />
					-N Emission Reductions (<CO2Units />)
				</span>
			),
			() => (
				<span>
					10-Year Project Benefits (<CO2Units />)
				</span>
			),
		],
		rows:
			fields &&
			fields.map((field) => [
				field.name,
				formatResult(field.results?.beforeN2O),
				formatResult(field.results?.afterN2O),
				formatResult(field.results?.avgYearlyN2OBenefits),
				formatResult(field.results?.tenYearN2OBenefits),
			]),
		totalsRow: [
			"Total",
			formatResult(reportData.n2oReport.beforeN2O),
			formatResult(reportData.n2oReport.afterN2O),
			formatResult(reportData.n2oReport.avgYearlyN2OBenefits),
			formatResult(reportData.n2oReport.tenYearN2OBenefits),
		],
	};
}

export function getWaterData(reportData, fields) {
	return {
		label: () => "Water Use",
		cardTitle: "Annual Irrigation Water Savings from Irrigation System Enhancements in Proposed Project",
		columnHeaders: [
			() => "Field",
			() => "Acres",
			() => "Pre-Project Water Use (ac-in/ac)",
			() => "Post-Project Water Use (ac-in/ac)",
			() => "Water Savings (ac-in/ac)",
			() => "Water Savings (ac-in)",
			() => "Water Savings (%)",
		],
		rows:
			fields &&
			fields.map((field) => [
				field.name,
				formatResult(field.acreage, true),
				formatResult(field.results?.beforeWater),
				formatResult(field.results?.afterWater),
				formatResult(field.results?.estWaterSavings),
				formatResult(field.results?.estWaterSavings * field.acreage),
				formatResult(field.results?.percentWaterSavings),
			]),
		totalsRow: [
			"Total",
			formatResult(reportData.waterReport.totalArea, true),
			formatResult(reportData.waterReport.beforeWaterUse / reportData.waterReport.totalArea),
			formatResult(reportData.waterReport.afterWaterUse / reportData.waterReport.totalArea),
			formatResult(reportData.waterReport.waterSavings / reportData.waterReport.totalArea),
			formatResult(reportData.waterReport.waterSavings),
			formatResult(reportData.waterReport.percentSavings),
		],
	};
}

export function getFieldData(fields) {
	return {
		label: () => "Field Data",
		cardTitle: "SWEEP Project Assessment Tool Field Data",
		columnHeaders: [
			() => "Field",
			() => "Acres",
			() => "Latitude",
			() => "Longitude",
			() => "Assembly District",
			() => "Senate District",
			() => "ET Zone",
			() => "Soil Type",
		],
		rows:
			fields &&
			fields.map((field) => [
				field.name,
				field.acreage,
				field.latLon[0].toFixed(5),
				field.latLon[1].toFixed(5),
				field.assemblyDistricts ? field.assemblyDistricts.join(", ") : loadingText,
				field.senateDistricts ? field.senateDistricts.join(", ") : loadingText,
				field.results ? field.results?.etZone.name.substring(8) : loadingText,
				field.results ? field.results?.soilType.name : loadingText,
			]),
	};
}
